import {
	ColumnDef,
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
	SortingState,
	Table as TanStackTable,
	useReactTable,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { ArrowDown, ArrowUp, SwitchVertical01 } from "@untitled-ui/icons-react";
import clsx from "clsx";

interface DataTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[];
	noResultsText: string;
	icon?: React.ElementType;
	variant?: "default" | "gray-no-pagination" | "default-with-sort" | "static-clean"  ;
	isLoading?: boolean;
	onRowClick?: (rowData: TData) => void;
	getRowClassName?: (rowData: TData) => string | undefined;
}

type DataTablePropsWithData<TData, TValue> = DataTableProps<TData, TValue> & {
	data: TData[];
	table?: never;
};

type DataTablePropsWithTable<TData, TValue> = DataTableProps<TData, TValue> & {
	data?: never;
	table?: TanStackTable<TData>;
};

export const DataTable = <TData, TValue>({
	columns,
	data,
	table,
	noResultsText,
	icon: Icon,
	variant = "default",
	isLoading = false,
	onRowClick,
	getRowClassName,
}: DataTablePropsWithData<TData, TValue> | DataTablePropsWithTable<TData, TValue>) => {
	const [sorting, setSorting] = useState<SortingState>([]);
 
	const instance = table || useReactTable({
		data: data || [],
		columns,
		state: {
			sorting,
		},
		onSortingChange: setSorting,
		getSortedRowModel: getSortedRowModel(),
		getCoreRowModel: getCoreRowModel(),
		enableSorting: variant === "default-with-sort",
	});

	const isGrayVariant = variant === "gray-no-pagination";
	const isStaticCleanVariant = variant === "static-clean";

	return (
		<div className="w-full min-w-0">
			<div
				className={`rounded-md border poppins font-medium overflow-hidden ${
					isGrayVariant ? "bg-gray-50" : "bg-white"
				}`}
			>
				<div className="overflow-x-auto">
					<Table>
						<TableHeader>
							{instance.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										const isSortable = header.column.getCanSort();
										const sortDirection = header.column.getIsSorted();
										return (
											<TableHead
												key={header.id}
												onClick={
													variant === "default-with-sort" && isSortable
														? header.column.getToggleSortingHandler()
														: undefined
												}
												className={`${
													isGrayVariant ? "px-3 py-4" : "p-4"
												} cursor-${isSortable ? "pointer" : "default"} select-none`}
											>
												<div className="flex items-center gap-1">
													{flexRender(header.column.columnDef.header, header.getContext())}
													{variant === "default-with-sort" && isSortable && (
														<span className="text-xs">
															{sortDirection === "asc" ? <ArrowUp className=" w-4" /> : sortDirection === "desc" ? <ArrowDown  className=" w-4" /> : <SwitchVertical01  className=" w-4" />}
														</span>
													)}
												</div>
											</TableHead>
										);
									})}
								</TableRow>
							))}
						</TableHeader>
						<TableBody>
						{isLoading ? (
								<TableRow>
									<TableCell
										colSpan={columns.length}
										className="h-24 text-center bg-white"
									>
										<div className="flex flex-col items-center justify-center gap-2 text-sm text-gray-500">
											<Loader2 className="animate-spin" />
											Chargement en cours...
										</div>
									</TableCell>
								</TableRow>
							) : instance.getRowModel().rows?.length ? (
								instance.getRowModel().rows.map((row, index) => (
									<TableRow
										key={row.id}
										className={clsx(
											isStaticCleanVariant
											? "bg-white"
											: isGrayVariant
											? "bg-gray-50"
											: index % 2 === 0
											? "bg-background-table-gray"
											: "bg-white",
											!isStaticCleanVariant && "transition-colors duration-150",
											onRowClick && "cursor-pointer hover:bg-gray-50/50",
											getRowClassName?.(row.original),
										)}
										data-state={row.getIsSelected() && "selected"}
										onClick={
											onRowClick
											? () => onRowClick(row.original) 
											: undefined
										}
									>
										{row.getVisibleCells().map((cell) => (
											<TableCell key={cell.id} className="p-4">
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</TableCell>
										))}
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell
										colSpan={columns.length}
										className="h-24 text-center bg-headerButtonActive"
									>
										<div className="flex flex-col items-center justify-center">
											{Icon && <Icon />}
											<span>{noResultsText}</span>
										</div>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>
		</div>
	);
};

export default DataTable;
